/**
 * یک اتصال WebSocket مشترک برای همهٔ کامپوننت‌ها.
 * با بستن/باز شدن مکرر کامپوننت (مثلاً React Strict Mode) اتصال بسته نمی‌شود و subscribe به سرور می‌رسد.
 */

import { getWebSocketUrl } from "./client";

/** @type {WebSocket | null} */
let ws = null;
/** @type {string} */
let wsUrl = "";
/** @type {Map<number, Set<(data: { ticketId: number; message: object }) => void>>} */
const callbacksByTicket = new Map();
/** @type {Set<number>} */
const subscribedTicketIds = new Set();
/** @type {Set<number>} current online remote_ids from server */
const onlineUserIds = new Set();
/** @type {Map<number, string>} last_seen_at ISO when user_offline was received this session */
const offlineLastSeenByRemoteId = new Map();
/** @type {Set<(ids: Set<number>) => void>} presence listeners */
const presenceCallbacks = new Set();
/** @type {Set<(remoteId: number, lastSeenAt: string) => void>} */
const presenceOfflineCallbacks = new Set();
/** @type {number | null} sent to server on this connection (resend on reconnect) */
let identifiedRemoteId = null;
const HEARTBEAT_INTERVAL_MS = 25000;
let heartbeatTimer = null;
/** @type {Map<string, object>} de-duplicated outbound payloads to flush on open */
const pendingPayloads = new Map();

/** @type {Set<(status: { state: string; error: boolean; canReconnect: boolean; message: string; updatedAt: number; }) => void>} */
const connectionStatusCallbacks = new Set();

let connectionStatus = {
  state: "idle",
  error: false,
  canReconnect: false,
  message: "",
  updatedAt: Date.now(),
};

/** @type {Map<number, Map<number, string>>} ticketId -> (remoteId -> "typing"|"recording") */
const typingStatusByTicket = new Map();
/** @type {Set<(ticketId: number, remoteId: number, status: string | null) => void>} */
const typingStatusCallbacks = new Set();

/** @type {Set<(evt: { type: string; alert?: object; alert_id?: string; meta?: object }) => void>} */
const externalAlertCallbacks = new Set();

/** @type {Set<(evt: { id: number; title: string; body: string; image_url: string; created_at: string }) => void>} */
const announcementCallbacks = new Set();

/** @type {Set<(evt: { type: string; call: object | null; durationSec: number | null; raw: object }) => void>} */
const voiceCallCallbacks = new Set();

/** @type {Set<(update: object) => void>} ticket list preview updates */
const ticketListUpdateCallbacks = new Set();

/** @type {Set<(payload: { ticketId: number; messageIds: number[]; read_at: string | null }) => void>} */
const messagesReadCallbacks = new Set();

function notifyTicketListUpdate(update) {
  if (!update || typeof update !== "object") return;
  ticketListUpdateCallbacks.forEach((cb) => {
    try {
      cb(update);
    } catch (_) {}
  });
}

function notifyMessagesRead(payload) {
  if (!payload || payload.ticketId == null) return;
  messagesReadCallbacks.forEach((cb) => {
    try {
      cb(payload);
    } catch (_) {}
  });
}

export function publishLocalMessagesRead(ticketId) {
  const id = Number(ticketId);
  if (!id || Number.isNaN(id)) return;
  notifyMessagesRead({
    type: "messages_read",
    ticketId: id,
    messageIds: [],
    read_at: new Date().toISOString(),
  });
}

function notifyExternalAlert(evt) {
  externalAlertCallbacks.forEach((cb) => {
    try {
      cb(evt);
    } catch (_) {}
  });
}

function notifyAnnouncement(evt) {
  announcementCallbacks.forEach((cb) => {
    try {
      cb(evt);
    } catch (_) {}
  });
}

function setConnectionStatus(nextStatus) {
  connectionStatus = {
    ...connectionStatus,
    ...nextStatus,
    updatedAt: Date.now(),
  };
  const snapshot = { ...connectionStatus };
  connectionStatusCallbacks.forEach((cb) => {
    try {
      cb(snapshot);
    } catch (_) {}
  });
}

function getCloseMessage(event) {
  if (event?.reason) return `اتصال وب‌سوکت قطع شد: ${event.reason}`;
  if (event?.code === 1000) return "اتصال دریافت پیام‌ها پایان یافت.";
  return "دریافت پیام‌های جدید متوقف شده است.";
}

function notifyTypingStatus(ticketId, remoteId, status) {
  const tid = Number(ticketId);
  const rid = Number(remoteId);
  if (!tid || isNaN(tid) || !rid || isNaN(rid)) return;
  let map = typingStatusByTicket.get(tid);
  if (status) {
    if (!map) {
      map = new Map();
      typingStatusByTicket.set(tid, map);
    }
    map.set(rid, status);
  } else {
    if (map) {
      map.delete(rid);
      if (map.size === 0) typingStatusByTicket.delete(tid);
    }
  }
  typingStatusCallbacks.forEach((cb) => {
    try {
      cb(tid, rid, status);
    } catch (_) {}
  });
}

function notifyPresence() {
  const snapshot = new Set(onlineUserIds);
  presenceCallbacks.forEach((cb) => {
    try {
      cb(snapshot);
    } catch (_) {}
  });
}

function notifyUserOffline(remoteId, lastSeenAt) {
  presenceOfflineCallbacks.forEach((cb) => {
    try {
      cb(remoteId, lastSeenAt);
    } catch (_) {}
  });
}

function clearHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function startHeartbeat() {
  clearHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "heartbeat" }));
      } catch (_) {}
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function getPendingPayloadKey(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.type === "mark_read" && payload.ticketId != null) {
    return `mark_read:${Number(payload.ticketId)}`;
  }
  return null;
}

function flushPendingPayloads() {
  if (!ws || ws.readyState !== WebSocket.OPEN || pendingPayloads.size === 0) return;
  for (const [key, payload] of pendingPayloads.entries()) {
    try {
      ws.send(JSON.stringify(payload));
      pendingPayloads.delete(key);
    } catch (_) {}
  }
}

function sendSubscribe(ticketId) {
  if (ws && ws.readyState === WebSocket.OPEN && !subscribedTicketIds.has(ticketId)) {
    ws.send(JSON.stringify({ type: "subscribe", ticketId }));
    subscribedTicketIds.add(ticketId);
  }
}

function sendUnsubscribe(ticketId) {
  if (ws && ws.readyState === WebSocket.OPEN && subscribedTicketIds.has(ticketId)) {
    ws.send(JSON.stringify({ type: "unsubscribe", ticketId }));
    subscribedTicketIds.delete(ticketId);
  }
}

function ensureConnected(url) {
  if (ws && ws.url === url) {
    return ws;
  }
  if (ws) {
    try {
      ws.close();
    } catch (_) {}
    ws = null;
    wsUrl = "";
    subscribedTicketIds.clear();
    clearHeartbeat();
  }
  wsUrl = url;
  setConnectionStatus({
    state: "connecting",
    error: false,
    canReconnect: false,
    message: "در حال اتصال به دریافت پیام‌های جدید...",
  });
  ws = new WebSocket(url);
  ws.onopen = () => {
    startHeartbeat();
    setConnectionStatus({
      state: "connected",
      error: false,
      canReconnect: false,
      message: "",
    });
    if (identifiedRemoteId != null) {
      try {
        ws.send(JSON.stringify({ type: "identify", remote_id: identifiedRemoteId }));
      } catch (_) {}
    }
    for (const ticketId of callbacksByTicket.keys()) {
      if (!subscribedTicketIds.has(ticketId)) {
        try {
          ws.send(JSON.stringify({ type: "subscribe", ticketId }));
          subscribedTicketIds.add(ticketId);
        } catch (_) {}
      }
    }
    flushPendingPayloads();
  };
  ws.onmessage = (e) => {
  try {
      const data = JSON.parse(e.data);
      if (data.type === "new_message" && data.ticketId != null && data.message) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = { type: "new_message", ticketId: id, message: data.message };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "message_updated" && data.ticketId != null && data.message) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = { type: "message_updated", ticketId: id, message: data.message };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "message_deleted" && data.ticketId != null && data.message_id != null) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = {
            type: "message_deleted",
            ticketId: id,
            messageId: Number(data.message_id),
          };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "messages_read" && data.ticketId != null && Array.isArray(data.message_ids)) {
        const id = Number(data.ticketId);
        const payload = {
          type: "messages_read",
          ticketId: id,
          messageIds: data.message_ids.map((m) => Number(m)).filter((n) => !Number.isNaN(n)),
          read_at: data.read_at || null,
        };
        notifyMessagesRead(payload);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "message_feedback_updated" && data.ticketId != null && data.message_id != null) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = {
            type: "message_feedback_updated",
            ticketId: id,
            messageId: Number(data.message_id),
            feedback_emoji: data.feedback_emoji || null,
            satisfaction: typeof data.satisfaction === "number" ? data.satisfaction : null,
            feedback_updated_at: data.feedback_updated_at || null,
          };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "pinned_message_updated" && data.ticketId != null) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = {
            type: "pinned_message_updated",
            ticketId: id,
            pinned_message: data.pinned_message || null,
          };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "block_status_updated" && data.ticketId != null) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = {
            type: "block_status_updated",
            ticketId: id,
            action: data.action,
            blocker_remote_id: data.blocker_remote_id,
            blocked_remote_id: data.blocked_remote_id,
            block: data.block || null,
          };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (
        (data.type === "copilot_suggestions_updated" ||
          data.type === "copilot_suggestions_stale") &&
        data.ticketId != null
      ) {
        const id = Number(data.ticketId);
        const cbs = callbacksByTicket.get(id);
        if (cbs) {
          const payload = {
            type: data.type,
            ticketId: id,
            generation: data.generation != null ? Number(data.generation) : 0,
            based_on_message_id:
              data.based_on_message_id != null ? Number(data.based_on_message_id) : null,
          };
          cbs.forEach((cb) => cb(payload));
        }
      } else if (data.type === "user_online" && data.remote_id != null) {
        onlineUserIds.add(Number(data.remote_id));
        notifyPresence();
      } else if (data.type === "user_offline" && data.remote_id != null) {
        const offlineId = Number(data.remote_id);
        const lastSeenAt =
          typeof data.last_seen_at === "string" && data.last_seen_at
            ? data.last_seen_at
            : new Date().toISOString();
        onlineUserIds.delete(offlineId);
        offlineLastSeenByRemoteId.set(offlineId, lastSeenAt);
        notifyPresence();
        notifyUserOffline(offlineId, lastSeenAt);
      } else if (data.type === "online_users" && Array.isArray(data.remote_ids)) {
        onlineUserIds.clear();
        data.remote_ids.forEach((id) => onlineUserIds.add(Number(id)));
        notifyPresence();
      } else if (data.type === "typing_status" && data.ticketId != null && data.remote_id != null) {
        notifyTypingStatus(data.ticketId, data.remote_id, data.status || null);
      } else if (data.type === "tickets_seen_changed" && Array.isArray(data.ticket_ids)) {
        // خوانده / خوانده‌نشده از دستگاهِ دیگرِ همین کاربر
        try {
          window.dispatchEvent(
            new CustomEvent("hamiTicketsSeenChanged", {
              detail: { ticketIds: data.ticket_ids.map(Number), seen: !!data.seen },
            })
          );
        } catch {
          // ignore
        }
      } else if (data.type === "ticket_list_update" && data.ticketId != null) {
        if (data.event === "new_message" && data.sender_remote_id != null) {
          notifyTypingStatus(data.ticketId, data.sender_remote_id, null);
        }
        notifyTicketListUpdate({ type: "ticket_list_update", ...data });
      } else if (typeof data.type === "string" && data.type.startsWith("voice_call.")) {
        const evt = {
          type: data.type,
          call: data.call && typeof data.call === "object" ? data.call : null,
          durationSec: data.durationSec ?? data.duration_sec ?? null,
          raw: data,
        };
        voiceCallCallbacks.forEach((cb) => {
          try {
            cb(evt);
          } catch (_) {}
        });
      } else if (data.type === "external_alert" && data.alert && data.alert.id != null) {
        notifyExternalAlert({
          type: "external_alert",
          alert: data.alert,
          meta: data.meta && typeof data.meta === "object" ? data.meta : {},
        });
      } else if (data.type === "external_alert_dismiss" && data.alert_id != null) {
        notifyExternalAlert({
          type: "external_alert_dismiss",
          alert_id: String(data.alert_id),
        });
      } else if (data.type === "announcement_updated" && data.id != null) {
        notifyAnnouncement({
          type: "announcement_updated",
          id: Number(data.id),
          announcement: data.announcement && typeof data.announcement === "object" ? data.announcement : null,
        });
      } else if (data.type === "announcement_deleted" && data.id != null) {
        notifyAnnouncement({ type: "announcement_deleted", id: Number(data.id) });
      } else if (data.type === "announcement" && data.id != null) {
        notifyAnnouncement({
          type: "announcement",
          id: Number(data.id),
          title: data.title || "",
          body: data.body || "",
          image_url: data.image_url || "",
          created_at: data.created_at || new Date().toISOString(),
        });
      }
    } catch (_) {}
  };
  ws.onclose = (event) => {
    clearHeartbeat();
    ws = null;
    subscribedTicketIds.clear();
    setConnectionStatus({
      state: "closed",
      error: true,
      canReconnect: !!wsUrl,
      message: getCloseMessage(event),
    });
  };
  ws.onerror = () => {
    setConnectionStatus({
      state: "error",
      error: true,
      canReconnect: !!wsUrl,
      message: "اتصال دریافت پیام‌ها با خطا مواجه شد.",
    });
  };
  return ws;
}

/**
 * Subscribe to new_message for a ticket. Returns an unsubscribe function.
 * @param {string} url - WebSocket URL
 * @param {number} ticketId
 * @param {(data: { ticketId: number; message: object }) => void} onNewMessage
 * @returns {() => void} unsubscribe
 */
export function subscribeToTicket(url, ticketId, onNewMessage) {
  const id = Number(ticketId);
  if (!id || isNaN(id)) return () => {};

  if (!callbacksByTicket.has(id)) {
    callbacksByTicket.set(id, new Set());
  }
  callbacksByTicket.get(id).add(onNewMessage);

  ensureConnected(url);
  sendSubscribe(id);

  return () => {
    const cbs = callbacksByTicket.get(id);
    if (cbs) {
      cbs.delete(onNewMessage);
      if (cbs.size === 0) {
        callbacksByTicket.delete(id);
        sendUnsubscribe(id);
      }
    }
  };
}

/**
 * Current snapshot of online user remote_ids (from presence).
 * @returns {Set<number>}
 */
export function getOnlineUserIds() {
  return new Set(onlineUserIds);
}

/**
 * Subscribe to presence updates. Callback receives current Set of online remote_ids.
 * @param {(ids: Set<number>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeToPresence(callback) {
  presenceCallbacks.add(callback);
  try {
    callback(new Set(onlineUserIds));
  } catch (_) {}
  return () => {
    presenceCallbacks.delete(callback);
  };
}

/**
 * Subscribe to user_offline events with a client-side last_seen_at (now unless server sends one).
 * @param {(remoteId: number, lastSeenAt: string) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeToPresenceOffline(callback) {
  presenceOfflineCallbacks.add(callback);
  return () => {
    presenceOfflineCallbacks.delete(callback);
  };
}

/**
 * Last seen recorded when user_offline was received this session (if any).
 * @param {number} remoteId
 * @returns {string | null}
 */
export function getOfflineLastSeen(remoteId) {
  if (remoteId == null) return null;
  return offlineLastSeenByRemoteId.get(Number(remoteId)) ?? null;
}

/**
 * Ensure WebSocket is connected (for presence even when no ticket is subscribed).
 * @param {string} url - WebSocket URL from getWebSocketUrl()
 */
export function ensureConnection(url) {
  if (url) ensureConnected(url);
}

let connectionSyncing = false;
/** @type {Set<(syncing: boolean) => void>} */
const connectionSyncingCallbacks = new Set();

/** @type {(() => void) | null} */
let reconnectHandler = null;

/** @type {Set<() => void>} */
const connectionRestoredCallbacks = new Set();

export function setConnectionSyncing(syncing) {
  const next = !!syncing;
  if (connectionSyncing === next) return;
  connectionSyncing = next;
  connectionSyncingCallbacks.forEach((cb) => {
    try {
      cb(connectionSyncing);
    } catch (_) {}
  });
}

export function subscribeToConnectionSyncing(callback) {
  connectionSyncingCallbacks.add(callback);
  try {
    callback(connectionSyncing);
  } catch (_) {}
  return () => {
    connectionSyncingCallbacks.delete(callback);
  };
}

export function registerReconnectHandler(handler) {
  reconnectHandler = handler;
  return () => {
    if (reconnectHandler === handler) reconnectHandler = null;
  };
}

export function requestReconnect() {
  if (typeof reconnectHandler === "function") {
    reconnectHandler();
    return;
  }
  reconnectConnection(wsUrl || getWebSocketUrl());
}

export function notifyConnectionRestored() {
  connectionRestoredCallbacks.forEach((cb) => {
    try {
      cb();
    } catch (_) {}
  });
}

export function subscribeToConnectionRestored(callback) {
  connectionRestoredCallbacks.add(callback);
  return () => {
    connectionRestoredCallbacks.delete(callback);
  };
}

export function reconnectConnection(url = wsUrl || getWebSocketUrl()) {
  if (!url) return;
  ensureConnected(url);
}

export function getConnectionStatus() {
  return { ...connectionStatus };
}

export function subscribeToConnectionStatus(callback) {
  connectionStatusCallbacks.add(callback);
  try {
    callback({ ...connectionStatus });
  } catch (_) {}
  return () => {
    connectionStatusCallbacks.delete(callback);
  };
}

/**
 * Send identify to server so this connection is associated with the user.
 * Call when WS URL and currentUserRemoteId are available. Resent automatically on reconnect.
 * @param {number} remoteId
 */
export function identify(remoteId) {
  const id = remoteId == null ? null : Number(remoteId);
  if (id != null && !isNaN(id)) {
    identifiedRemoteId = id;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "identify", remote_id: id }));
      } catch (_) {}
    }
  }
}

function sendPayload(payload) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify(payload));
      const pendingKey = getPendingPayloadKey(payload);
      if (pendingKey) pendingPayloads.delete(pendingKey);
    } catch (_) {}
    return;
  }

  const pendingKey = getPendingPayloadKey(payload);
  if (pendingKey) {
    pendingPayloads.set(pendingKey, payload);
  }
}

/**
 * Notify server that current user is typing in this ticket. Ensures WS connection before sending.
 * @param {number} ticketId
 * @param {boolean} active
 */
export function sendTyping(ticketId, active) {
  const id = Number(ticketId);
  if (!id || isNaN(id)) return;
  ensureConnected(getWebSocketUrl());
  sendPayload({ type: "typing", ticketId: id, active: !!active });
}

/**
 * Notify server that current user is recording voice in this ticket. Ensures WS connection before sending.
 * @param {number} ticketId
 * @param {boolean} active
 */
export function sendRecording(ticketId, active) {
  const id = Number(ticketId);
  if (!id || isNaN(id)) return;
  ensureConnected(getWebSocketUrl());
  sendPayload({ type: "recording", ticketId: id, active: !!active });
}

export function sendMarkRead(ticketId) {
  const id = Number(ticketId);
  if (!id || isNaN(id)) return;
  ensureConnected(getWebSocketUrl());
  sendPayload({ type: "mark_read", ticketId: id });
}

/**
 * Get current typing/recording status for a ticket. Returns Map of remoteId -> "typing"|"recording".
 * @param {number} ticketId
 * @returns {Map<number, string>}
 */
export function getTypingStatus(ticketId) {
  const id = Number(ticketId);
  if (!id || isNaN(id)) return new Map();
  const map = typingStatusByTicket.get(id);
  return map ? new Map(map) : new Map();
}

/**
 * Subscribe to typing/recording updates. Callback(ticketId, remoteId, status) where status is "typing"|"recording"|null.
 * @param {(ticketId: number, remoteId: number, status: string | null) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeToTypingStatus(callback) {
  typingStatusCallbacks.add(callback);
  return () => {
    typingStatusCallbacks.delete(callback);
  };
}

/**
 * پیام‌های external_alert / external_alert_dismiss از سرور (اعلان هدفمند).
 * @param {(evt: { type: string; alert?: object; alert_id?: string; meta?: object }) => void} callback
 * @returns {() => void}
 */
export function subscribeToExternalAlerts(callback) {
  externalAlertCallbacks.add(callback);
  return () => {
    externalAlertCallbacks.delete(callback);
  };
}

/**
 * رویدادهای آنیِ اطلاع‌رسانی پذیرش۲۴ (فقط کاربرانی که آنلاین‌اند):
 * - type "announcement": خبر تازه، با id/title/body/image_url/created_at
 * - type "announcement_updated": ویرایش یک خبر منتشرشده، با announcement (شکل کامل API)
 * - type "announcement_deleted": حذف یا برگشتن خبر به صف تأیید، فقط id
 * @param {(evt: { type: "announcement" | "announcement_updated" | "announcement_deleted"; id: number; [key: string]: unknown }) => void} callback
 * @returns {() => void}
 */
export function subscribeToAnnouncements(callback) {
  announcementCallbacks.add(callback);
  return () => {
    announcementCallbacks.delete(callback);
  };
}

/**
 * Real-time ticket list preview updates (new/edit/delete message).
 * @param {(update: object) => void} callback
 * @returns {() => void}
 */
export function subscribeToTicketListUpdates(callback) {
  ticketListUpdateCallbacks.add(callback);
  return () => {
    ticketListUpdateCallbacks.delete(callback);
  };
}

/**
 * Apply a ticket_list_update locally (e.g. after current user's send/close in open chat).
 * Uses the same subscribers as WebSocket ticket_list_update events.
 * @param {object} update
 */
export function publishLocalTicketListUpdate(update) {
  notifyTicketListUpdate(update);
}

/**
 * Global messages_read events (for chats list unread badge reset).
 * @param {(payload: { ticketId: number; messageIds: number[]; read_at: string | null }) => void} callback
 * @returns {() => void}
 */
export function subscribeToMessagesRead(callback) {
  messagesReadCallbacks.add(callback);
  return () => {
    messagesReadCallbacks.delete(callback);
  };
}

/**
 * رویدادهای تماس صوتی (voice_call.ringing / accepted / rejected / ended / missed).
 * @param {(evt: { type: string; call: object | null; durationSec: number | null; raw: object }) => void} callback
 * @returns {() => void}
 */
export function subscribeToVoiceCallEvents(callback) {
  voiceCallCallbacks.add(callback);
  return () => {
    voiceCallCallbacks.delete(callback);
  };
}
